// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import resetReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ResetReCaptcha";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { parseBoolean } from "@steiner/XDL/Utilities";
import React, { RefObject, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    location: string;
    recaptchaRef: RefObject<ReCAPTCHA | null>;
    extendedStyles?: ReCaptchaStyles;
    // XNG-Changes: STR-2731
    onTokenChange?: (token: string | null) => void;
}

// XNG-Changes: added custom reCaptchaSitekey + stnrEnableReCaptcha settings alongside ootb ones to match what classic is using
const mapStateToProps = (state: ApplicationState, { location, recaptchaRef }: OwnProps) => {
    const { customSettings, websiteSettings } = getSettingsCollection(state);
    const session = getSession(state);
    return {
        siteKey: (customSettings as any).stnrReCaptchaSiteKey || websiteSettings.reCaptchaSiteKey,
        isEnabled:
            parseBoolean((customSettings as any).stnrEnableReCaptcha) ||
            (websiteSettings as { [key: string]: any })[`reCaptchaEnabledFor${location}`],
        isAuthenticated: session && (session.isAuthenticated || session.rememberMe) && !session.isGuest,
        errorMessage: state.components.reCaptcha.errorMessage[location],
        recaptchaRef,
        location,
    };
};

const mapDispatchToProps = {
    validateReCaptcha,
    resetReCaptcha,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ReCaptchaStyles {
    wrapper?: InjectableCss;
    errorMessageText?: TypographyPresentationProps;
}

export const reCaptchaStyles: ReCaptchaStyles = {
    wrapper: {
        css: css`
            min-width: 304px;
            min-height: 78px;
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 5px;
            font-size: 15px;
            font-weight: 600;
        `,
    },
};

const ReCaptcha = ({
    siteKey,
    isEnabled,
    isAuthenticated,
    errorMessage,
    recaptchaRef,
    location,
    validateReCaptcha,
    resetReCaptcha,
    extendedStyles,
    // XNG-Changes: STR-2731
    onTokenChange,
}: Props) => {
    const [styles] = useState(() => mergeToNew(reCaptchaStyles, extendedStyles));
    const [firstRender, setFirstRender] = useState(true);

    useEffect(() => {
        setFirstRender(false);
        resetReCaptcha({ location });
    }, []);

    const handleSuccess = (token: string | null) => {
        validateReCaptcha({ location, recaptchaRef });
        // XNG-Changes: STR-2731
        onTokenChange?.(token);
    };

    /*
       https://xngage.atlassian.net/browse/STNRS-860
       Displaying the recaptcha for the authenticated users
    */
    if (firstRender || !isEnabled || !!getCookie("g-recaptcha-verified")) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <ReCAPTCHA ref={recaptchaRef} sitekey={siteKey} onChange={handleSuccess} />
            {errorMessage && (
                <Typography as="p" {...styles.errorMessageText}>
                    {errorMessage}
                </Typography>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ReCaptcha);
